<template>
    <div class="inventory app__page">
        <div class="app__page-header">
            <sah-button @click.native="newApplication" class="app-page-header__button">{{
                $tc("applications.addApplication")
            }}</sah-button>
            <sah-button
                @click.native="openImportApplications"
                v-if="$features.importApplications"
                class="app-page-header__button"
                >{{ $tc("applications.importApplications") }}
            </sah-button>
            <div class="inventory__filters-bar">
                <applications-search
                    class="inventory__search"
                    :label="$tc(`applicationsFilters.search`)"
                ></applications-search>
                <applications-sorter
                    class="inventory__sorter"
                    :fields="$settings.dataModels.INVENTORY_APPLICATIONS_SORT_FIELDS"
                ></applications-sorter>
                <applications-filters
                    class="inventory__filters"
                    :applications="filteredApplications"
                    :adaptiveFilters="false"
                ></applications-filters>
            </div>
        </div>
        <div class="inventory__applications-counters">
            {{ $t("applications.applicationsCounters", {filteredApplicationsCounter, applicationsCounter}) }}
        </div>
        <div class="inventory__app-list app__cards-list">
            <application-show
                v-for="application in filteredApplications"
                :key="application.id"
                :application="application"
                @editApplication="editApplication"
            ></application-show>
        </div>
        <application-edit
            v-if="isEditingApplication"
            :operation="editOperation"
            :application="applicationToEdit"
            @cancel="closeApplicationEdit"
            @end="closeApplicationEdit"
        ></application-edit>
        <import-applications
            v-if="isImportingApplications"
            @cancel="closeImportApplications"
            @end="closeImportApplications"
        ></import-applications>
    </div>
</template>

<script>
/**
 * Renderer for the landing page of the application
 */
import ApplicationShow from "../components/ApplicationShow.vue";
import ApplicationEdit from "../components/ApplicationEdit.vue";
import ApplicationsFilters from "../components/ApplicationsFilters.vue";
import ApplicationsSearch from "../components/ApplicationsSearch.vue";
import ApplicationsSorter from "../components/ApplicationsSorter.vue";
import {store, SahButton} from "atom";
import {
    APPLICATIONS_TYPE_VERSIONS_EXCLUDED_FIELDS,
    EDIT_OPERATION_STORE,
    EDIT_OPERATION_UPDATE,
    VERSION_NUMBER_PATTERN,
} from "../constants";

const {createNamespacedHelpers} = store;
const {mapGetters, mapState} = createNamespacedHelpers("arcman");
import ImportApplications from "@/components/ImportApplications.vue";

export default {
    name: "inventory",
    components: {
        ApplicationShow,
        ApplicationEdit,
        ApplicationsFilters,
        ApplicationsSearch,
        ApplicationsSorter,
        SahButton,
        ImportApplications,
    },
    data() {
        return {
            isEditingApplication: false,
            isEditingVersion: false,
            isImportingApplications: false,
            applicationToEdit: {},
            versionToEdit: {},
            editOperation: "",
        };
    },
    computed: {
        ...mapState(["applications", "arcologyConstants", "filteredApplications"]),
        ...mapGetters({showSpinner: "showApplicationsSpinner"}),
        applicationsCounter() {
            return this.applications.length;
        },
        filteredApplicationsCounter() {
            return this.filteredApplications.length;
        },
    },
    watch: {
        showSpinner(newValue, oldValue) {
            this.$emit("showSpinner", newValue);
        },
    },
    methods: {
        getCleanNewApplication() {
            const application = Object.assign(
                JSON.parse(JSON.stringify(this.arcologyConstants.APPLICATION_TEMPLATE)),
                JSON.parse(JSON.stringify(this.$settings.dataModels.DEFAULT_APPLICATION))
            );
            application.versions = [this.getCleanNewVersion(application.type)];
            return application;
        },
        getCleanNewVersion(applicationType) {
            const version = {};
            const versionTemplate = JSON.parse(JSON.stringify(this.arcologyConstants.VERSION_TEMPLATE));
            for (const [key, value] of Object.entries(versionTemplate)) {
                if (!APPLICATIONS_TYPE_VERSIONS_EXCLUDED_FIELDS[applicationType].has(key)) {
                    version[key] = value;
                }
            }
            const icon = JSON.parse(JSON.stringify(this.arcologyConstants.PICTURE_TEMPLATE));
            version.icons.push(icon);
            version.date = new Date().toISOString().split("T")[0];
            version.versionNumber = VERSION_NUMBER_PATTERN;
            return Object.assign(version, JSON.parse(JSON.stringify(this.$settings.dataModels.DEFAULT_VERSION)));
        },
        newApplication() {
            this.editOperation = EDIT_OPERATION_STORE;
            this.isEditingApplication = true;
            this.applicationToEdit = this.getCleanNewApplication();
        },
        editApplication(event) {
            this.isEditingApplication = true;
            this.editOperation = EDIT_OPERATION_UPDATE;
            this.applicationToEdit = Object.assign(this.getCleanNewApplication(), event);
        },
        closeApplicationEdit() {
            this.isEditingApplication = false;
            this.editOperation = "";
            this.applicationToEdit = {};
        },
        openImportApplications() {
            this.isImportingApplications = true;
        },
        closeImportApplications() {
            this.isImportingApplications = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.inventory::v-deep {
    .inventory__filters-bar {
        @apply flex flex-col w-full my-6 items-center;
        background-color: $background-application-color;
        border-bottom: 0.125rem solid $border-filters-color;
        @screen lg {
            @apply flex flex-row w-full items-end;
        }
    }

    .inventory__search {
        @apply w-full;
        @screen lg {
            width: $search-box-width;
        }
    }

    .inventory__search,
    .inventory__sorter,
    .inventory__filters {
        @apply mr-2 mb-2;
    }

    .inventory__applications-counters {
        @apply w-full;
    }
}
</style>
